import { Loading } from "@components/common/Loading";
import { useEffect, type FC } from "react";
import { FormInstanceRenderer } from "@nomena-raminoheritiana/front-form-builder";
import type { IRecruiterRegisterFormProps } from "@components/ui/recruiter/components/recruiter-form/interface";
import { Box } from "@mui/material";
import { useLanguage } from "../../../../../contexts/LanguageContext";
import { removeFieldGrid } from "@utils/removeFieldGrid";

const RecruiterRegisterForm: FC<IRecruiterRegisterFormProps> = ({ 
  onAfterSubmit, 
  formBuilderAccessToken, 
  defaultValue 
}) => {

  const { language } = useLanguage();

  const handleAfterSubmit = (data: any) => {
    onAfterSubmit && onAfterSubmit(data);
  };

  useEffect(() => {
        const cleanDOM = () => {
            const container = document.getElementById('recruiter-register-form');
            if (!container) return;

            removeFieldGrid(container, 'otp');

            return true;
        };

        // Exécution initiale
        cleanDOM();

        // Observation pour gérer le rendu asynchrone du FormInstanceRenderer
        const container = document.getElementById('recruiter-register-form');
        if (!container) return;

        const observer = new MutationObserver(() => {
            cleanDOM();
        });

        observer.observe(container, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, []);

  return (
    <Box
      id="recruiter-register-form"
      sx={{
        width: '100%',
        backgroundColor: '#171d25',
        p: { xs: 3, sm: 4 },
        // Injection de styles CSS pour forcer le rendu du FormInstanceRenderer en dark mode
        '& .MuiTypography-root': {
          color: '#ffffff',
        },
        '& .MuiFormLabel-root': {
          color: '#94a3b8',
          '&.Mui-focused': {
            color: '#1976d2',
          },
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '8px',
          color: '#ffffff',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&:hover fieldset': {
            borderColor: '#1976d2',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#1976d2',
          },
        },
        '& .MuiInputBase-input::placeholder': {
          color: '#64748b',
          opacity: 1,
        },
        '& .MuiButton-root': {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          py: 1.2,
          '&.MuiButton-contained': {
            backgroundColor: '#1976d2',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#115293',
            },
          },
        },
      }}
    >

      {/* Rendu dynamique du formulaire */}
      <FormInstanceRenderer
        formInstanceId={`${import.meta.env.VITE_RECRUITER_FORM_INSTANCE_ID}`}
        defaultValues={defaultValue}
        showUnnamedFields={false}
        accessToken={formBuilderAccessToken || undefined}
        formLoader={() => <Loading />}
        lang={language}
        onSubmit={(data) => {
          handleAfterSubmit(data);
        }}
      />
    </Box>
  );
};

export default RecruiterRegisterForm;